import { HttpStatusCode } from 'axios';
import { call, put, select } from 'redux-saga/effects';

import CONFIG from 'app.config';

import { extractErrorMessage, isErrorResponse, sendToLogger } from 'connect/saga/helpers';
import { selectDataLoadingUser, selectDataUser } from 'connect/selectors';
import Logger from 'utils/Logger';
import { makeRequest } from 'utils/request';

import { decodeBase64ToObject } from 'utils/string-utils';

import { SagaHandler } from '../SagaHandler';

interface SimpleUser {
  authenticated?: boolean;
  id?: string;
}

const getAuthParam = () =>
  makeRequest<void, { authParam: string; }>({
    method: 'GET',
    url: `${CONFIG.API_URL}/user/auth-param`,
  });

const getPatientV4User = () =>
  makeRequest<void, SimpleUser>({
    method: 'GET',
    url: `${CONFIG.PATIENT_V4_APP_URL}/api/user`,
  });

interface LoginPatientV4Payload {
  email: string;
  token: string;
}

const loginPatientV4 = (payload: LoginPatientV4Payload) =>
  makeRequest<LoginPatientV4Payload, SimpleUser>({
    method: 'POST',
    url: `${CONFIG.PATIENT_V4_APP_URL}/api/loginToken`,
    data: {
      email: payload.email,
      token: payload.token,
    },
  });

const logoutPatientV4 = () =>
  makeRequest<void, void>({
    method: 'POST',
    url: `${CONFIG.PATIENT_V4_APP_URL}/api/logout`,
  });

interface SagaPayload {
  payload: {
    shouldLogout?: boolean;
  };
}

class _PatientV4Login extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/LOGIN_PATIENT_V4',
      stateKey: 'loginPatientV4',
    });
  }

  * onSuccess(user: SimpleUser): Generator {
    const {
      value: valueHandler,
      success: successHandler,
    } = this;

    yield put(valueHandler!.update({
      authenticated: Boolean(user.id),
      id: user.id,
    }));
    yield put(successHandler!.update(true));
  }

  * tryLoginToPatientV4(email: string, token: string): Generator {
    try {
      // Login to the patient v4 app
      const loginResponse = yield call(loginPatientV4, {
        email,
        token,
      });
      const didSucceed = Boolean(loginResponse?.id);
      if (!didSucceed) {
        const error = new Error('Could not login to patient v4 app');
        Logger.error(error, { loginResponse });
        throw error;
      }

      return loginResponse;
    } catch (error) {
      const isAlreadyLoggedIn = isErrorResponse(error) && error.response?.status === HttpStatusCode.Forbidden;
      if (!isAlreadyLoggedIn) {
        sendToLogger(error, 'tryLoginToPatientV4');
        throw error;
      }

      return { authenticated: false, id: null };
    }
  }

  * tryLogoutFromPatientV4(): Generator {
    try {
      yield call(logoutPatientV4);
    } catch (error) {
      const isAlreadyLoggedOut = isErrorResponse(error) && error.response?.status === HttpStatusCode.Forbidden;
      if (!isAlreadyLoggedOut) {
        sendToLogger(error, 'tryLogoutFromPatientV4');
        throw error;
      }
    }
  }

  * saga({ payload }: SagaPayload): Generator {
    const {
      error: errorHandler,
      loading: loadingHandler,
      value: valueHandler,
      success: successHandler,
    } = this;

    try {
      const isLoadingUser = yield select(selectDataLoadingUser);
      if (isLoadingUser) {
        return;
      }

      yield put(errorHandler!.update(null));
      yield put(successHandler!.update(false));
      yield put(loadingHandler!.update(true));

      // An optimization, if the previous value has a loaded user, and it's the same as the current user,
      // we can skip the rest of the saga
      const reduxUser: SimpleUser | undefined = yield select(selectDataUser);
      const previousValue: SimpleUser | undefined = yield select(valueHandler!.selector);
      const canReusePreviousValue = isSameUser(previousValue, reduxUser);
      if (canReusePreviousValue) {
        yield this.onSuccess(reduxUser as SimpleUser);
        return;
      }

      yield put(valueHandler!.update({ authenticated: false, id: null }));

      // Load the user from the patient v4 app
      const patientV4UserResponse: SimpleUser = yield call(getPatientV4User);
      const isAlreadyAuthenticated = isSameUser(reduxUser, patientV4UserResponse);
      if (isAlreadyAuthenticated) {
        yield this.onSuccess(patientV4UserResponse);
        return;
      }

      // Logout if the caller has requested it,
      // or if the user is already logged into patient v4 as a different user
      const isLoggedIntoPatientV4 = Boolean(patientV4UserResponse?.id);
      const isLoggedIntoPatientV4AsDifferentUser = (
        isLoggedIntoPatientV4
        && !isSameUser(reduxUser, patientV4UserResponse)
      );
      const shouldLogout = Boolean(payload?.shouldLogout) || isLoggedIntoPatientV4AsDifferentUser;
      if (shouldLogout) {
        yield this.tryLogoutFromPatientV4();
      }

      // Load the auth param from the API
      const authParamResponse = yield call(getAuthParam);
      if (!authParamResponse?.authParam) {
        throw new Error('Could not get auth param');
      }

      // Decode the auth param
      const { authParam } = authParamResponse;
      const decodedAuthParam = decodeURIComponent(authParam);
      const authData = decodeBase64ToObject(decodedAuthParam) as { email?: string; token?: string; };
      if (!authData?.email || !authData?.token) {
        throw new Error('Could not decode auth param');
      }

      // Login to the patient v4 app
      const loginResponse = yield this.tryLoginToPatientV4(authData.email, authData.token);

      yield this.onSuccess(loginResponse);
    } catch (error) {
      sendToLogger(error, 'patientV4Login');
      const errorMessage = extractErrorMessage(error);
      yield put(errorHandler!.update(errorMessage));
    } finally {
      yield put(loadingHandler!.update(false));
    }
  }
}

const isSameUser = (
  user: SimpleUser | undefined,
  userResponse: SimpleUser | undefined,
) => {
  if (!user?.id || !userResponse?.id) {
    return false;
  }

  return user.id === userResponse.id;
};

export const PatientV4Login = new _PatientV4Login();

